// AGC: TaskRecommendationsAdmin - Admin panel for task category recommendations
// AGC: Manages machinery and product recommendations per task category
// AGC: Used by voice command pre-modal to show relevant items in "Recommended" tabs
// AGC: Default machinery mappings sourced from shared activityRules (single source of truth).

import React, { useState, useEffect, useCallback } from 'react'
import { useTranslation } from '../../hooks/useTranslation'
import {
  Save,
  Check,
  AlertCircle,
  Loader2,
  RefreshCw,
  Wrench,
  Package,
  ChevronDown,
  ChevronRight,
} from 'lucide-react'
import { getFirestore, doc, getDoc, setDoc } from 'firebase/firestore'
import { ACTIVITY_RULES } from '@droidfarm/shared/activityRules'

// AGC: Type for machinery category mapping
interface MachineryMapping {
  taskCategory: string
  taskLabel: string
  machineTypes: string[] | null // AGC: null means all types
}

// AGC: Type for product/input category mapping
interface ProductMapping {
  taskCategory: string
  taskLabel: string
  inputCategories: string[] | null // AGC: null means all categories
}

// AGC: Machine type labels for display
const MACHINE_TYPE_LABELS: Record<string, string> = {
  trator: '🚜 Trator',
  pulverizador: '💨 Pulverizador',
  plantadeira: '🌱 Plantadeira',
  semeadora: '🌾 Semeadora',
  colheitadeira: '🌾 Colheitadeira',
  distribuidor: '📦 Distribuidor',
  implemento: '⚙️ Implemento',
  irrigacao: '💧 Irrigação',
  caminhao: '🚚 Caminhão',
  outros: '📋 Outros',
}

// AGC: Input category labels for display
const INPUT_CATEGORY_LABELS: Record<string, string> = {
  herbicide: '🌿 Herbicida',
  fungicide: '🍄 Fungicida',
  insecticide: '🐛 Inseticida',
  acaricide: '🕷️ Acaricida',
  nematicide: '🪱 Nematicida',
  fertilizer: '🧪 Fertilizante',
  soil_amendment: '🌍 Corretivo',
  seed: '🌱 Semente',
  inoculant: '🦠 Inoculante',
  adjuvant: '💧 Adjuvante',
  fuel: '⛽ Combustível',
  machinery_parts: '🔧 Peças',
  packaging: '📦 Embalagens',
  other: '📋 Outro',
}

// AGC: Task category labels
const TASK_CATEGORY_LABELS: Record<string, string> = {
  pulverizacao: '💨 Pulverização',
  plantio: '🌱 Plantio',
  colheita: '🌾 Colheita',
  dessecacao: '🍂 Dessecação',
  adubacao: '🧪 Adubação',
  preparo_solo: '🚜 Preparo do Solo',
  irrigacao: '💧 Irrigação',
  manutencao: '🔧 Manutenção',
  monitoramento: '👁️ Monitoramento',
  outros: '📋 Outros',
}

// AGC: Default machinery mappings derived from the shared ACTIVITY_RULES (single source of truth).
// AGC: Replaces the former hardcoded constant that duplicated taskValidator.js.
// AGC: 'any' mode → null (show all). Otherwise, flatten eligibleCategories into string[].
const DEFAULT_MACHINERY_MAPPINGS: MachineryMapping[] = Object.values(ACTIVITY_RULES).map(rule => ({
  taskCategory: rule.taskCategory,
  taskLabel: TASK_CATEGORY_LABELS[rule.taskCategory] ?? rule.taskCategory,
  machineTypes: rule.mode === 'any' ? null : [
    ...new Set(rule.allowedCombos.flat()),
    ...(rule.tratorFallback ? ['trator'] : []),
  ] as string[],
}))

// AGC: Default product mappings (from taskValidator.js)
const DEFAULT_PRODUCT_MAPPINGS: ProductMapping[] = [
  { taskCategory: 'pulverizacao', taskLabel: 'Pulverização', inputCategories: ['herbicide', 'fungicide', 'insecticide', 'acaricide', 'nematicide', 'adjuvant'] },
  { taskCategory: 'plantio', taskLabel: 'Plantio', inputCategories: ['seed', 'inoculant', 'fertilizer'] },
  { taskCategory: 'adubacao', taskLabel: 'Adubação', inputCategories: ['fertilizer', 'soil_amendment'] },
  { taskCategory: 'dessecacao', taskLabel: 'Dessecação', inputCategories: ['herbicide', 'adjuvant'] },
  { taskCategory: 'preparo_solo', taskLabel: 'Preparo do Solo', inputCategories: ['fertilizer', 'soil_amendment'] },
  { taskCategory: 'irrigacao', taskLabel: 'Irrigação', inputCategories: ['fertilizer', 'adjuvant'] },
  { taskCategory: 'colheita', taskLabel: 'Colheita', inputCategories: ['fuel'] },
  { taskCategory: 'manutencao', taskLabel: 'Manutenção', inputCategories: ['fuel', 'machinery_parts'] },
  { taskCategory: 'monitoramento', taskLabel: 'Monitoramento', inputCategories: null },
  { taskCategory: 'outros', taskLabel: 'Outros', inputCategories: null },
]

/**
 * AGC: TaskRecommendationsAdmin - Admin component for task recommendations
 * AGC: Allows editing machinery and product category mappings per task category
 */
export const TaskRecommendationsAdmin: React.FC = () => {
  const { t } = useTranslation()
  
  // AGC: State for data
  const [machineryMappings, setMachineryMappings] = useState<MachineryMapping[]>(DEFAULT_MACHINERY_MAPPINGS)
  const [productMappings, setProductMappings] = useState<ProductMapping[]>(DEFAULT_PRODUCT_MAPPINGS)
  
  // AGC: UI state
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [expandedMachinery, setExpandedMachinery] = useState<string[]>([])
  const [expandedProducts, setExpandedProducts] = useState<string[]>([])
  const [editingMachinery, setEditingMachinery] = useState<string | null>(null)
  const [editingProducts, setEditingProducts] = useState<string | null>(null)
  // AGC: Top-level section collapse state (Machinery and Products sections)
  const [machineryCollapsed, setMachineryCollapsed] = useState(false)
  const [productsCollapsed, setProductsCollapsed] = useState(false)
  
  // AGC: Load data from Firestore
  const loadData = useCallback(async () => {
    setLoading(true)
    setError(null)
    
    try {
      const db = getFirestore()
      
      // AGC: Load machinery mappings
      const machineryDoc = await getDoc(doc(db, 'config', 'taskMachineryRecommendations'))
      if (machineryDoc.exists()) {
        const data = machineryDoc.data()
        if (data.mappings && Array.isArray(data.mappings)) {
          setMachineryMappings(data.mappings)
        }
      }
      
      // AGC: Load product mappings
      const productsDoc = await getDoc(doc(db, 'config', 'taskProductRecommendations'))
      if (productsDoc.exists()) {
        const data = productsDoc.data()
        if (data.mappings && Array.isArray(data.mappings)) {
          setProductMappings(data.mappings)
        }
      }
    } catch (err) {
      console.error('AGC: Error loading recommendations:', err)
      setError(t('admin.recommendations.loadError', 'Erro ao carregar recomendações'))
    } finally {
      setLoading(false)
    }
  }, [t])
  
  // AGC: Load on mount
  useEffect(() => {
    loadData()
  }, [loadData])
  
  // AGC: Save machinery mappings to Firestore
  const saveMachineryMappings = async () => {
    setSaving(true)
    setError(null)
    setSuccess(null)
    
    try {
      const db = getFirestore()
      await setDoc(doc(db, 'config', 'taskMachineryRecommendations'), {
        mappings: machineryMappings,
        updatedAt: new Date().toISOString(),
      })
      setSuccess(t('admin.recommendations.saveSuccess', 'Recomendações de maquinário salvas!'))
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      console.error('AGC: Error saving machinery recommendations:', err)
      setError(t('admin.recommendations.saveError', 'Erro ao salvar recomendações'))
    } finally {
      setSaving(false)
    }
  }
  
  // AGC: Save product mappings to Firestore
  const saveProductMappings = async () => {
    setSaving(true)
    setError(null)
    setSuccess(null)
    
    try {
      const db = getFirestore()
      await setDoc(doc(db, 'config', 'taskProductRecommendations'), {
        mappings: productMappings,
        updatedAt: new Date().toISOString(),
      })
      setSuccess(t('admin.recommendations.saveSuccess', 'Recomendações de produtos salvas!'))
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      console.error('AGC: Error saving product recommendations:', err)
      setError(t('admin.recommendations.saveError', 'Erro ao salvar recomendações'))
    } finally {
      setSaving(false)
    }
  }
  
  // AGC: Reset to defaults
  const resetToDefaults = () => {
    if (window.confirm(t('admin.recommendations.confirmReset', 'Tem certeza que deseja restaurar os padrões?'))) {
      setMachineryMappings(DEFAULT_MACHINERY_MAPPINGS)
      setProductMappings(DEFAULT_PRODUCT_MAPPINGS)
      setSuccess(t('admin.recommendations.resetSuccess', 'Restaurado para padrões. Clique em Salvar para confirmar.'))
      setTimeout(() => setSuccess(null), 3000)
    }
  }
  
  // AGC: Toggle machine type for a task category
  const toggleMachineType = (taskCategory: string, machineType: string) => {
    setMachineryMappings(prev => prev.map(mapping => {
      if (mapping.taskCategory !== taskCategory) return mapping
      
      // AGC: If currently null (all), start with empty array
      let current = mapping.machineTypes || []
      
      if (current.includes(machineType)) {
        // AGC: Remove
        current = current.filter(t => t !== machineType)
      } else {
        // AGC: Add
        current = [...current, machineType]
      }
      
      // AGC: If all types selected, set to null
      const allTypes = Object.keys(MACHINE_TYPE_LABELS)
      const isAll = allTypes.every(t => current.includes(t))
      
      return { ...mapping, machineTypes: isAll ? null : current }
    }))
  }
  
  // AGC: Toggle input category for a task category
  const toggleInputCategory = (taskCategory: string, inputCategory: string) => {
    setProductMappings(prev => prev.map(mapping => {
      if (mapping.taskCategory !== taskCategory) return mapping
      
      // AGC: If currently null (all), start with empty array
      let current = mapping.inputCategories || []
      
      if (current.includes(inputCategory)) {
        // AGC: Remove
        current = current.filter(c => c !== inputCategory)
      } else {
        // AGC: Add
        current = [...current, inputCategory]
      }
      
      // AGC: If all categories selected, set to null
      const allCategories = Object.keys(INPUT_CATEGORY_LABELS)
      const isAll = allCategories.every(c => current.includes(c))
      
      return { ...mapping, inputCategories: isAll ? null : current }
    }))
  }
  
  // AGC: Set all types/categories for a task
  const setAllMachineTypes = (taskCategory: string) => {
    setMachineryMappings(prev => prev.map(mapping => 
      mapping.taskCategory === taskCategory ? { ...mapping, machineTypes: null } : mapping
    ))
  }
  
  const setAllInputCategories = (taskCategory: string) => {
    setProductMappings(prev => prev.map(mapping => 
      mapping.taskCategory === taskCategory ? { ...mapping, inputCategories: null } : mapping
    ))
  }
  
  // AGC: Clear all types/categories for a task
  const clearMachineTypes = (taskCategory: string) => {
    setMachineryMappings(prev => prev.map(mapping => 
      mapping.taskCategory === taskCategory ? { ...mapping, machineTypes: [] } : mapping
    ))
  }
  
  const clearInputCategories = (taskCategory: string) => {
    setProductMappings(prev => prev.map(mapping => 
      mapping.taskCategory === taskCategory ? { ...mapping, inputCategories: [] } : mapping
    ))
  }
  
  // AGC: Toggle expansion
  const toggleMachineryExpand = (taskCategory: string) => {
    setExpandedMachinery(prev => 
      prev.includes(taskCategory) 
        ? prev.filter(c => c !== taskCategory)
        : [...prev, taskCategory]
    )
  }
  
  const toggleProductsExpand = (taskCategory: string) => {
    setExpandedProducts(prev => 
      prev.includes(taskCategory) 
        ? prev.filter(c => c !== taskCategory)
        : [...prev, taskCategory]
    )
  }
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
        <span className="ml-2 text-slate-600 dark:text-slate-300">{t('common.loading', 'Carregando...')}</span>
      </div>
    )
  }
  
  return (
    <div className="space-y-6">
      {/* AGC: Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-200 flex items-center gap-2">
            ⭐ {t('admin.recommendations.title', 'Recomendações de Tarefas')}
          </h2>
          <p className="text-slate-600 dark:text-slate-300 mt-1">
            {t('admin.recommendations.description', 'Configure quais maquinários e produtos são recomendados para cada tipo de tarefa.')}
          </p>
        </div>
        <button
          onClick={resetToDefaults}
          className="px-4 py-2 bg-slate-200 dark:bg-white/10 hover:bg-slate-300 dark:hover:bg-white/[0.15] rounded-lg text-slate-700 dark:text-slate-300 transition-colors flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          {t('admin.recommendations.resetDefaults', 'Restaurar Padrões')}
        </button>
      </div>
      
      {/* AGC: Error/Success messages */}
      {error && (
        <div className="p-4 bg-error-50 dark:bg-red-500/15 border border-red-200 dark:border-red-500/30 rounded-lg flex items-center gap-2 text-error-700 dark:text-red-400">
          <AlertCircle className="w-5 h-5" />
          {error}
        </div>
      )}
      {success && (
        <div className="p-4 bg-success-50 dark:bg-emerald-500/15 border border-green-200 dark:border-emerald-500/30 rounded-lg flex items-center gap-2 text-brand-primary dark:text-emerald-400">
          <Check className="w-5 h-5" />
          {success}
        </div>
      )}
      
      {/* AGC: Machinery Recommendations */}
      <div className="bg-white dark:bg-admin-card-dark rounded-xl shadow-card dark:shadow-none border border-slate-200 dark:border-white/5 overflow-hidden">
        <div
          className="px-6 py-4 bg-emerald-50 dark:bg-emerald-500/10 border-b border-emerald-100 dark:border-emerald-500/20 flex items-center justify-between cursor-pointer"
          onClick={() => setMachineryCollapsed(prev => !prev)}
        >
          <div className="flex items-center gap-2">
            {machineryCollapsed ? <ChevronRight className="w-5 h-5 text-emerald-600" /> : <ChevronDown className="w-5 h-5 text-emerald-600" />}
            <Wrench className="w-5 h-5 text-emerald-600" />
            <h3 className="text-lg font-semibold text-emerald-800 dark:text-emerald-300">
              {t('admin.recommendations.machinery', 'Maquinário por Categoria de Tarefa')}
            </h3>
            <span className="text-sm text-emerald-600 dark:text-emerald-400">({machineryMappings.length})</span>
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); saveMachineryMappings() }}
            disabled={saving}
            className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 rounded-lg text-white transition-colors flex items-center gap-2"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {t('common.save', 'Salvar')}
          </button>
        </div>
        
        {!machineryCollapsed && (
        <div className="divide-y divide-slate-100 dark:divide-white/5">
          {machineryMappings.map(mapping => {
            const isExpanded = expandedMachinery.includes(mapping.taskCategory)
            const selectedCount = mapping.machineTypes === null 
              ? Object.keys(MACHINE_TYPE_LABELS).length 
              : mapping.machineTypes.length
            const totalCount = Object.keys(MACHINE_TYPE_LABELS).length
            
            return (
              <div key={mapping.taskCategory} className="hover:bg-slate-50/50 dark:hover:bg-white/[0.02]">
                <button
                  onClick={() => toggleMachineryExpand(mapping.taskCategory)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left"
                >
                  <div className="flex items-center gap-3">
                    {isExpanded ? <ChevronDown className="w-4 h-4 text-slate-400 dark:text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-400 dark:text-slate-500" />}
                    <span className="text-lg">{TASK_CATEGORY_LABELS[mapping.taskCategory] || mapping.taskCategory}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 rounded text-xs ${
                      mapping.machineTypes === null
                        ? 'bg-info-50 dark:bg-blue-500/15 text-info-700 dark:text-blue-400'
                        : selectedCount > 0
                          ? 'bg-success-50 dark:bg-emerald-500/15 text-brand-primary dark:text-emerald-400'
                          : 'bg-slate-100 dark:bg-white/[0.04] text-slate-600 dark:text-slate-300'
                    }`}>
                      {mapping.machineTypes === null 
                        ? t('admin.recommendations.allTypes', 'Todos os tipos')
                        : `${selectedCount}/${totalCount} ${t('admin.recommendations.types', 'tipos')}`
                      }
                    </span>
                  </div>
                </button>
                
                {isExpanded && (
                  <div className="px-6 pb-4 pt-2 bg-slate-50 dark:bg-white/[0.02]">
                    <div className="flex gap-2 mb-3">
                      <button
                        onClick={() => setAllMachineTypes(mapping.taskCategory)}
                        className="px-3 py-1 text-xs bg-info-50 dark:bg-blue-500/15 hover:bg-blue-200 dark:hover:bg-blue-500/20 text-info-700 dark:text-blue-400 rounded transition-colors"
                      >
                        {t('admin.recommendations.selectAll', 'Selecionar Todos')}
                      </button>
                      <button
                        onClick={() => clearMachineTypes(mapping.taskCategory)}
                        className="px-3 py-1 text-xs bg-slate-100 dark:bg-white/[0.04] hover:bg-slate-200 dark:hover:bg-white/10 text-slate-700 dark:text-slate-300 rounded transition-colors"
                      >
                        {t('admin.recommendations.clearAll', 'Limpar Todos')}
                      </button>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
                      {Object.entries(MACHINE_TYPE_LABELS).map(([key, label]) => {
                        const isSelected = mapping.machineTypes === null || mapping.machineTypes.includes(key)
                        return (
                          <button
                            key={key}
                            onClick={() => toggleMachineType(mapping.taskCategory, key)}
                            className={`px-3 py-2 rounded-lg text-sm transition-colors flex items-center gap-2 ${
                              isSelected
                                ? 'bg-emerald-500 dark:bg-emerald-600 text-white'
                                : 'bg-white dark:bg-white/[0.04] border border-slate-200 dark:border-white/10 text-slate-700 dark:text-slate-300 hover:border-emerald-300 dark:hover:border-emerald-500/50'
                            }`}
                          >
                            {isSelected && <Check className="w-4 h-4" />}
                            {label}
                          </button>
                        )
                      })}
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
        )}
      </div>
      
      {/* AGC: Product Recommendations */}
      <div className="bg-white dark:bg-admin-card-dark rounded-xl shadow-card dark:shadow-none border border-slate-200 dark:border-white/5 overflow-hidden">
        <div
          className="px-6 py-4 bg-info-50 dark:bg-blue-500/10 border-b border-blue-100 dark:border-blue-500/20 flex items-center justify-between cursor-pointer"
          onClick={() => setProductsCollapsed(prev => !prev)}
        >
          <div className="flex items-center gap-2">
            {productsCollapsed ? <ChevronRight className="w-5 h-5 text-blue-600" /> : <ChevronDown className="w-5 h-5 text-blue-600" />}
            <Package className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-semibold text-blue-800 dark:text-blue-300">
              {t('admin.recommendations.products', 'Produtos/Insumos por Categoria de Tarefa')}
            </h3>
            <span className="text-sm text-blue-600 dark:text-blue-400">({productMappings.length})</span>
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); saveProductMappings() }}
            disabled={saving}
            className="px-4 py-2 bg-blue-500 hover:bg-blue-600 disabled:opacity-50 rounded-lg text-white transition-colors flex items-center gap-2"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {t('common.save', 'Salvar')}
          </button>
        </div>
        
        {!productsCollapsed && (
        <div className="divide-y divide-slate-100 dark:divide-white/5">
          {productMappings.map(mapping => {
            const isExpanded = expandedProducts.includes(mapping.taskCategory)
            const selectedCount = mapping.inputCategories === null 
              ? Object.keys(INPUT_CATEGORY_LABELS).length 
              : mapping.inputCategories.length
            const totalCount = Object.keys(INPUT_CATEGORY_LABELS).length
            
            return (
              <div key={mapping.taskCategory} className="hover:bg-slate-50/50 dark:hover:bg-white/[0.02]">
                <button
                  onClick={() => toggleProductsExpand(mapping.taskCategory)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left"
                >
                  <div className="flex items-center gap-3">
                    {isExpanded ? <ChevronDown className="w-4 h-4 text-slate-400 dark:text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-400 dark:text-slate-500" />}
                    <span className="text-lg">{TASK_CATEGORY_LABELS[mapping.taskCategory] || mapping.taskCategory}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 rounded text-xs ${
                      mapping.inputCategories === null
                        ? 'bg-info-50 dark:bg-blue-500/15 text-info-700 dark:text-blue-400'
                        : selectedCount > 0
                          ? 'bg-success-50 dark:bg-emerald-500/15 text-brand-primary dark:text-emerald-400'
                          : 'bg-slate-100 dark:bg-white/[0.04] text-slate-600 dark:text-slate-300'
                    }`}>
                      {mapping.inputCategories === null 
                        ? t('admin.recommendations.allCategories', 'Todas as categorias')
                        : `${selectedCount}/${totalCount} ${t('admin.recommendations.categories', 'categorias')}`
                      }
                    </span>
                  </div>
                </button>
                
                {isExpanded && (
                  <div className="px-6 pb-4 pt-2 bg-slate-50 dark:bg-white/[0.02]">
                    <div className="flex gap-2 mb-3">
                      <button
                        onClick={() => setAllInputCategories(mapping.taskCategory)}
                        className="px-3 py-1 text-xs bg-info-50 dark:bg-blue-500/15 hover:bg-blue-200 dark:hover:bg-blue-500/20 text-info-700 dark:text-blue-400 rounded transition-colors"
                      >
                        {t('admin.recommendations.selectAll', 'Selecionar Todos')}
                      </button>
                      <button
                        onClick={() => clearInputCategories(mapping.taskCategory)}
                        className="px-3 py-1 text-xs bg-slate-100 dark:bg-white/[0.04] hover:bg-slate-200 dark:hover:bg-white/10 text-slate-700 dark:text-slate-300 rounded transition-colors"
                      >
                        {t('admin.recommendations.clearAll', 'Limpar Todos')}
                      </button>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                      {Object.entries(INPUT_CATEGORY_LABELS).map(([key, label]) => {
                        const isSelected = mapping.inputCategories === null || mapping.inputCategories.includes(key)
                        return (
                          <button
                            key={key}
                            onClick={() => toggleInputCategory(mapping.taskCategory, key)}
                            className={`px-3 py-2 rounded-lg text-sm transition-colors flex items-center gap-2 ${
                              isSelected
                                ? 'bg-blue-500 dark:bg-blue-600 text-white'
                                : 'bg-white dark:bg-white/[0.04] border border-slate-200 dark:border-white/10 text-slate-700 dark:text-slate-300 hover:border-blue-300 dark:hover:border-blue-500/50'
                            }`}
                          >
                            {isSelected && <Check className="w-4 h-4" />}
                            {label}
                          </button>
                        )
                      })}
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
        )}
      </div>
      
      {/* AGC: Info section */}
      <div className="bg-warning-50 dark:bg-yellow-500/10 border border-amber-200 dark:border-yellow-500/20 rounded-xl p-4">
        <h4 className="font-medium text-warning-700 dark:text-yellow-400 flex items-center gap-2 mb-2">
          <AlertCircle className="w-5 h-5" />
          {t('admin.recommendations.howItWorks', 'Como funciona')}
        </h4>
        <ul className="text-sm text-warning-700 dark:text-yellow-400/80 space-y-1 ml-7 list-disc">
          <li>{t('admin.recommendations.info1', 'Quando um usuário cria uma tarefa por voz, o sistema mostra uma aba "Recomendados" com os maquinários e produtos relevantes.')}</li>
          <li>{t('admin.recommendations.info2', 'Se uma categoria de tarefa estiver configurada como "Todos", todas as opções serão mostradas como recomendadas.')}</li>
          <li>{t('admin.recommendations.info3', 'Se nenhuma opção estiver selecionada, a aba "Recomendados" não aparecerá para essa categoria de tarefa.')}</li>
          <li>{t('admin.recommendations.info4', 'Mudanças aqui afetam imediatamente todos os usuários do sistema.')}</li>
        </ul>
      </div>
    </div>
  )
}

export default TaskRecommendationsAdmin
